import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cn } from '../../../lib/utils';
import { specraFocusRing } from '../ensigns/focus-ring';
import { SpecraSwatch } from '../ensigns/swatch';
import { SpecraText } from '../ensigns/typography';
import { SpecraChartFrame, SpecraChartLegend, SpecraChartLink } from '../lieutenants/chart-frame';
import { SpecraTooltip, SpecraTooltipProvider } from '../lieutenants/tooltip';
/** Percent (0-100, clamped) of `value` on `domain`. Exported for tests and for consumers that annotate. */ export function rangeDotPlotPosition(value, domain) {
    const [lo, hi] = domain;
    if (hi === lo) return 0;
    const pct = (value - lo) / (hi - lo) * 100;
    return Math.min(100, Math.max(0, pct));
}
const defaultFormat = (v)=>`$${v}`;
/** Rows of shaped markers on one shared value axis, with an optional band and reference line. */ export function SpecraRangeDotPlot({ rows, domain, ticks, format = defaultFormat, band, reference, legend, emptyMessage, renderLink, className, getRowTestId, getPointTestId, title, description, actions, insight, 'data-testid': testId }) {
    const pos = (v)=>rangeDotPlotPosition(v, domain);
    const empty = rows.length === 0;
    const table = {
        columns: [
            {
                key: 'row',
                label: 'Row'
            },
            {
                key: 'point',
                label: 'Point'
            },
            {
                key: 'value',
                label: 'Value',
                numeric: true
            },
            {
                key: 'detail',
                label: 'Detail'
            }
        ],
        rows: rows.flatMap((r)=>r.points.map((p)=>({
                    id: `${r.id}:${p.id}`,
                    cells: {
                        row: r.sublabel ? `${r.label} · ${r.sublabel}` : r.label,
                        point: p.title,
                        value: format(p.value),
                        detail: p.detail ?? ''
                    }
                })))
    };
    const axis = /*#__PURE__*/ _jsxs("div", {
        className: "flex",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "w-[var(--component-chart-label-width)] shrink-0"
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "relative h-[var(--space-6)] min-w-0 flex-1",
                children: ticks.map((t)=>/*#__PURE__*/ _jsx(SpecraText, {
                        className: "absolute top-0 -translate-x-1/2 whitespace-nowrap tabular-nums",
                        size: "meta",
                        style: {
                            left: `${pos(t)}%`
                        },
                        tone: "muted",
                        children: format(t)
                    }, t))
            })
        ]
    });
    return /*#__PURE__*/ _jsx(SpecraChartFrame, {
        actions: actions,
        className: className,
        "data-testid": testId,
        description: description,
        insight: empty ? undefined : insight,
        legend: empty ? undefined : /*#__PURE__*/ _jsx(SpecraChartLegend, {
            items: legend
        }),
        table: table,
        title: title,
        children: empty ? /*#__PURE__*/ _jsxs("div", {
            className: "relative",
            "data-chart-empty": "",
            children: [
                axis,
                [
                    0,
                    1,
                    2
                ].map((k)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex h-[var(--component-dot-plot-row-height)] items-center border-b border-[color:var(--border-subtle)]",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "h-2 w-[var(--component-chart-label-width)] shrink-0 rounded-[var(--radius-xs)] bg-[var(--chart-heat-0)]"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "ml-[var(--space-3)] h-px flex-1 bg-[var(--chart-heat-0)]"
                            })
                        ]
                    }, k)),
                /*#__PURE__*/ _jsx("div", {
                    className: "absolute inset-0 flex items-center justify-center",
                    children: /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        className: "max-w-sm rounded-[var(--radius-md)] bg-[var(--surface-card)] px-[var(--space-2)] py-[var(--space-1)] text-center",
                        size: "body-ui",
                        tone: "muted",
                        children: emptyMessage
                    })
                })
            ]
        }) : /*#__PURE__*/ _jsx(SpecraTooltipProvider, {
            delayDuration: 100,
            children: /*#__PURE__*/ _jsx("div", {
                className: "overflow-x-auto",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "min-w-[calc(var(--component-chart-label-width)*2.5)]",
                    children: [
                        axis,
                        /*#__PURE__*/ _jsxs("div", {
                            className: "relative",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    "aria-hidden": "true",
                                    className: "pointer-events-none absolute inset-y-0 right-0 left-[var(--component-chart-label-width)]",
                                    children: [
                                        ticks.map((t)=>/*#__PURE__*/ _jsx("span", {
                                                className: "absolute inset-y-0 border-l border-[color:var(--chart-grid)]",
                                                "data-chart-tick": "",
                                                style: {
                                                    left: `${pos(t)}%`
                                                }
                                            }, t)),
                                        band ? /*#__PURE__*/ _jsx("span", {
                                            className: "absolute inset-y-0 border-x border-dashed border-[color:var(--chart-ours)]",
                                            "data-chart-band": "",
                                            style: {
                                                left: `${pos(band.from)}%`,
                                                width: `${pos(band.to) - pos(band.from)}%`,
                                                background: 'color-mix(in srgb, var(--chart-ours) 10%, transparent)'
                                            }
                                        }) : null,
                                        reference ? /*#__PURE__*/ _jsx("span", {
                                            className: "absolute inset-y-0 border-l border-dotted border-foreground",
                                            "data-chart-reference": "",
                                            style: {
                                                left: `${pos(reference.value)}%`
                                            }
                                        }) : null
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("ul", {
                                    "aria-label": title,
                                    className: "relative m-0 list-none p-0",
                                    children: rows.map((r)=>/*#__PURE__*/ _jsx(DotRow, {
                                            format: format,
                                            getPointTestId: getPointTestId,
                                            pos: pos,
                                            renderLink: renderLink,
                                            row: r,
                                            testId: getRowTestId?.(r)
                                        }, r.id))
                                })
                            ]
                        })
                    ]
                })
            })
        })
    });
}
function DotRow({ row, pos, format, renderLink, testId, getPointTestId }) {
    const span = row.span;
    const lo = span ? Math.min(span.from, span.to) : 0;
    const hi = span ? Math.max(span.from, span.to) : 0;
    return /*#__PURE__*/ _jsxs("li", {
        className: "flex h-[var(--component-dot-plot-row-height)] items-center border-b border-[color:var(--border-subtle)]",
        "data-item-id": row.id,
        "data-testid": testId,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "w-[var(--component-chart-label-width)] min-w-0 shrink-0 pr-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "div",
                        className: "truncate font-medium",
                        size: "body-sm",
                        title: row.label,
                        children: row.label
                    }),
                    row.sublabel ? /*#__PURE__*/ _jsx(SpecraText, {
                        as: "div",
                        className: "truncate",
                        size: "meta",
                        title: row.sublabel,
                        tone: "muted",
                        children: row.sublabel
                    }) : null
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "relative h-full min-w-0 flex-1",
                children: [
                    span ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                "aria-hidden": "true",
                                className: "absolute top-1/2 h-px bg-[color:var(--text-muted)]",
                                "data-chart-span": "",
                                style: {
                                    left: `${pos(lo)}%`,
                                    width: `${pos(hi) - pos(lo)}%`
                                }
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                className: "absolute top-[var(--space-0-5)] -translate-x-1/2 whitespace-nowrap font-medium",
                                size: "meta",
                                style: {
                                    left: `${(pos(span.from) + pos(span.to)) / 2}%`
                                },
                                children: span.label
                            })
                        ]
                    }) : null,
                    row.points.map((p)=>/*#__PURE__*/ _jsx(SpecraTooltip, {
                            content: /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    p.title,
                                    p.detail ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx("br", {}),
                                            p.detail
                                        ]
                                    }) : null
                                ]
                            }),
                            delayDuration: 100,
                            children: /*#__PURE__*/ _jsx(SpecraChartLink, {
                                "aria-label": p.title,
                                className: cn('absolute top-1/2 flex -translate-x-1/2 -translate-y-1/2 rounded-full', specraFocusRing),
                                "data-item-id": p.id,
                                "data-testid": getPointTestId?.(row, p),
                                "data-value": format(p.value),
                                href: p.href,
                                renderLink: renderLink,
                                role: p.href ? undefined : 'img',
                                style: {
                                    left: `${pos(p.value)}%`
                                },
                                tabIndex: 0,
                                children: /*#__PURE__*/ _jsx(SpecraSwatch, {
                                    shape: p.shape,
                                    size: "md",
                                    tone: p.tone
                                })
                            })
                        }, p.id))
                ]
            })
        ]
    });
}
